import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import PageGray from '../../components/General/PageGray';
import H1 from '../../components/Typography/Headings/H1';
import { useTranslation } from 'react-i18next';
import P from '../../components/Typography/P';
import { Search } from '@testbusters/search';
import Span from '../../components/Typography/Span';
import { useState, useEffect, useRef, useMemo } from 'react';
import { cn } from '../../lib/utills';
import { useGetAttempts } from '../../hooks/attempts/useAttempts';
import { useGetTrainingAttemptsList } from '../../hooks/trainingAttempts/useTrainingAttempts';
import InProgressAttempts from '../../components/SimulationsList/InProgress/InProgressAttempts';
import ToDoCollections from '../../components/SimulationsList/ToDo/ToDoCollections';
import Scrollbar from '../../components/SimulationsList/Scrollbar/Scrollbar';
import CompletedAttempts from '../../components/SimulationsList/CompletedAttempts/CompletedAttempts';
import SimulationCard from '../../components/SimulationsList/SimulationCard/SimulationCard';
import { useSimulationsFilter } from '../../hooks/useSimulationsFilter';
import { useScrollbarSections } from '../../hooks/useScrollbarSections';
import GeneralError from '../../components/Errors/General';
import Loader from '../../components/Loader/Loader';
import { useGetCollections, useGetCollectionFilters } from '../../hooks/collections/useCollections';
import { CollectionType } from '@/types/collection';
import { Chip } from '@testbusters/chip';
import { Icon } from '@testbusters/icon';
import { useTestContext } from '../../context/TestContext';
import { toast } from 'sonner';
import ErrorNoCollections from '../../components/Errors/ErrorNoCollections';
import { Button } from '@testbusters/button';
import { useAuth } from '@/auth/AuthContext';
import { pushGtmEvent } from '@/lib/gtm';

export const Route = createFileRoute('/_sidebar/esercitazioni')({
  validateSearch: (search: Record<string, unknown>): { filters?: string } => ({
    filters: typeof search.filters === 'string' ? search.filters : undefined,
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { filters: filtersParam } = Route.useSearch();
  const initialTags = filtersParam
    ? filtersParam.split(',').map((f) => f.trim()).filter(Boolean)
    : [];
  const { selectedTestId, isFetching: isTestFetching } = useTestContext();
  const [officialTestsOnly, setOfficialTestsOnly] = useState(false);

  const {
    collections,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error,
  } = useGetCollections({
    testId: selectedTestId,
    type: CollectionType.EXERCISE,
    officialTests: false,
    free: false,
    sort: 'name',
  });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (error) toast.error(t('testSelector.loadError'));
  }, [error]);

  const {
    collections: freeCollections,
    hasNextPage: freeHasNextPage,
    fetchNextPage: freeFetchNextPage,
    isFetchingNextPage: freeIsFetchingNextPage,
  } = useGetCollections({
    testId: selectedTestId,
    type: CollectionType.EXERCISE,
    free: true,
    sort: 'name',
  });

  useEffect(() => {
    if (freeHasNextPage && !freeIsFetchingNextPage) freeFetchNextPage();
  }, [freeHasNextPage, freeIsFetchingNextPage]);

  const {
    collections: officialCollections,
    hasNextPage: officialHasNextPage,
    fetchNextPage: officialFetchNextPage,
    isFetchingNextPage: officialIsFetchingNextPage,
  } = useGetCollections({
    testId: selectedTestId,
    type: CollectionType.EXERCISE,
    officialTests: true,
    sort: 'name',
  });

  useEffect(() => {
    if (officialHasNextPage && !officialIsFetchingNextPage) officialFetchNextPage();
  }, [officialHasNextPage, officialIsFetchingNextPage]);

  const allCollections = useMemo(() => {
    const seen = new Set<string>();
    return [...collections, ...freeCollections, ...officialCollections].filter((c) => {
      if (seen.has(c._id)) return false;
      seen.add(c._id);
      return true;
    });
  }, [collections, freeCollections, officialCollections]);

  const {
    attempts,
    isFetching: isAttemptsFetching,
    hasNextPage: attemptsHasNextPage,
    fetchNextPage: attemptsFetchNextPage,
    isFetchingNextPage: attemptsIsFetchingNextPage,
  } = useGetAttempts({ testId: selectedTestId, type: CollectionType.EXERCISE });

  useEffect(() => {
    if (attemptsHasNextPage && !attemptsIsFetchingNextPage)
      attemptsFetchNextPage();
  }, [attemptsHasNextPage, attemptsIsFetchingNextPage]);

  const {
    inProgress: inProgressTraining,
    completed: completedTraining,
    isFetching: isTrainingFetching,
  } = useGetTrainingAttemptsList();

  const {
    handleSearch,
    search,
    isSearchActive,
    filteredCollections,
    inProgressAttempts,
    toDoCollections,
    completedAttempts,
    inProgressTrainingAttempts,
    completedTrainingAttempts,
    selectedTags,
    handleTagToggle,
    resetFilters,
  } = useSimulationsFilter({
    collections: allCollections,
    attempts: attempts.filter(
      (a) => !a.collectionSnapshot || a.collectionSnapshot.type === CollectionType.EXERCISE,
    ),
    trainingAttempts: [...inProgressTraining, ...completedTraining],
    officialTestsOnly,
    initialTags,
  });

  const handleTagToggleWithUrl = (tag: string) => {
    const next = selectedTags.includes(tag)
      ? selectedTags.filter((t) => t !== tag)
      : [...selectedTags, tag];
    handleTagToggle(tag);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    navigate({ search: { filters: next.length ? next.join(',') : undefined } } as any);
  };

  const { filters: paidTags } = useGetCollectionFilters({
    testId: selectedTestId,
    type: CollectionType.EXERCISE,
  });

  const availableTags = useMemo(() => {
    const extra: string[] = [];
    if (freeCollections.length > 0) extra.push('Gratuite');
    if (officialCollections.length > 0) extra.push('Prove ufficiali');
    return [...extra, ...paidTags];
  }, [officialCollections.length, freeCollections.length, paidTags]);

  const prevTestIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (prevTestIdRef.current === undefined) {
      prevTestIdRef.current = selectedTestId ?? null;
      return;
    }
    if (prevTestIdRef.current === selectedTestId) return;
    prevTestIdRef.current = selectedTestId ?? null;
    handleSearch('');
    resetFilters();
    setActiveSection(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    navigate({ search: { filters: undefined } } as any);
    window.scrollTo({ top: 0 });
  }, [selectedTestId]);

  const { activeSection, setActiveSection } = useScrollbarSections({
    inProgressCount: inProgressAttempts.length + inProgressTrainingAttempts.length,
    toDoCount: toDoCollections.length,
    completedCount: completedAttempts.length + completedTrainingAttempts.length,
  });

  const countFilteredResults =
    inProgressAttempts.length +
    inProgressTrainingAttempts.length +
    toDoCollections.length +
    completedAttempts.length +
    completedTrainingAttempts.length;

  useEffect(() => {
    const stored = sessionStorage.getItem('officialTestsOnly');
    if (stored !== null) setOfficialTestsOnly(stored === 'true');
  }, []);

  const isEmpty =
    inProgressAttempts.length === 0 &&
    inProgressTraining.length === 0 &&
    toDoCollections.length === 0 &&
    completedAttempts.length === 0 &&
    completedTraining.length === 0;

  if (
    isTestFetching ||
    (isFetching && collections.length === 0) ||
    (isAttemptsFetching && attempts.length === 0) ||
    isTrainingFetching
  )
    return <Loader fullScreen />;
  if (error) return <GeneralError />;

  return (
    <PageGray className="flex flex-col gap-6">
      <div className="flex flex-col gap-7">
        <div className="flex flex-col gap-4">
          <H1>{t('common.exercises')}</H1>
          <P>{t('simulationsList.subtitleExercise')}</P>
        </div>
        {!isEmpty && (
          <div className="flex flex-col-reverse lg:flex-row items-center gap-7">
            <Search
              onChange={(e) => handleSearch(e.target.value)}
              onClear={() => handleSearch('')}
              showClear
              placeholder={t('common.search')}
            />
            <Link
              to="/esercitazioni/crea"
              onClick={() => pushGtmEvent({ event: 'create_exercise_click', user_id: user?.profile.sub })}
            >
              <Button
                className="min-h-10"
                variant={'primary'}
              >
                {t('exercise.createButton')}
              </Button>
            </Link>
          </div>
        )}
        {availableTags.length > 0 && (
          <div className="max-w-full">
            <div className="flex items-center gap-4 overflow-x-scroll no-scrollbar">
              {availableTags.map((tag) => {
                const isSelected = selectedTags.includes(tag);
                return (
                  <Chip
                    key={tag}
                    onClick={() => handleTagToggleWithUrl(tag)}
                    className={cn(
                      'cursor-pointer border rounded-[10px] select-none font-normal transition-colors',
                      isSelected
                        ? ' border-background-accent bg-background-accent-disabled font-semibold! shadow-2xl!'
                        : 'bg-white border-border-input-field',
                    )}
                  >
                    <Span className="capitalize text-inherit">
                      {tag}
                    </Span>
                    {isSelected && (
                      <Icon
                        name="close"
                        className="text-xl"
                      />
                    )}
                  </Chip>
                );
              })}
            </div>
          </div>
        )}
      </div>
      {isEmpty ? (
        <ErrorNoCollections collectionType={CollectionType.EXERCISE} />
      ) : (
        <div className="flex flex-col gap-7">
          {isSearchActive ? (
            <Span className="font-semibold">
              {countFilteredResults} {t('common.resultsFor')} "
              <Span className="font-bold">{search}</Span>"
            </Span>
          ) : (
            <Scrollbar
              activeSection={activeSection}
              onTabChange={setActiveSection}
              inProgressCount={
                inProgressAttempts.length + inProgressTrainingAttempts.length
              }
              toDoCount={toDoCollections.length}
              completedCount={
                completedAttempts.length + completedTrainingAttempts.length
              }
            />
          )}
          <div className="flex flex-col gap-5">
            {(isSearchActive || activeSection === 'toDo') && (
              <div className="flex flex-col gap-5">
                {/* {!isSearchActive && toDoCollections.length > 0 && (
                  <Span className="font-semibold">
                    {t('simulationsList.toDoExercises', {
                      count: toDoCollections.length,
                    })}
                  </Span>
                )} */}
                <ToDoCollections
                  collections={toDoCollections}
                  disablePagination={isSearchActive}
                />
              </div>
            )}
            {(isSearchActive || activeSection === 'inProgress') && (
              <div className="flex flex-col gap-5">
                {!isSearchActive &&
                  inProgressAttempts.length + inProgressTrainingAttempts.length > 0 && (
                    <Span className="font-semibold">
                      {t('simulationsList.inProgressExercises', {
                        count:
                          inProgressAttempts.length + inProgressTrainingAttempts.length,
                      })}
                    </Span>
                  )}
                {(inProgressAttempts.length > 0 ||
                  inProgressTrainingAttempts.length > 0) && (
                  <InProgressAttempts
                    collections={filteredCollections}
                    attempts={inProgressAttempts}
                    disablePagination={isSearchActive}
                  />
                )}
                {inProgressTrainingAttempts.map(({ attempt, collection }) => (
                  <SimulationCard
                    key={attempt._id}
                    collection={collection}
                    attempt={attempt}
                    isTrainingAttempt
                  />
                ))}
              </div>
            )}
            {(isSearchActive || activeSection === 'completed') && (
              <div className="flex flex-col gap-5">
                {!isSearchActive &&
                  completedAttempts.length + completedTrainingAttempts.length > 0 && (
                    <Span className="font-semibold">
                      {t('simulationsList.completedExercises', {
                        count:
                          completedAttempts.length + completedTrainingAttempts.length,
                      })}
                    </Span>
                  )}
                {completedAttempts.length > 0 && (
                  <CompletedAttempts
                    collections={filteredCollections}
                    attempts={completedAttempts}
                    disablePagination={isSearchActive}
                  />
                )}
                {completedTrainingAttempts.map(({ attempt, collection }) => (
                  <SimulationCard
                    key={attempt._id}
                    collection={collection}
                    attempt={attempt}
                    isTrainingAttempt
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </PageGray>
  );
}
